import { action } from "@ember/object"
import { scheduleOnce } from "@ember/runloop"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type {
  Card,
  CardGoogleClassroom,
  CardStack,
  GoogleClassroomPostType,
  LessonCard,
} from "@shared/models/card-stack"
import type { Permission } from "@shared/models/permissions"
import type { Planbook } from "@shared/models/planbook"
import ObjectId from "@shared/util/object-id-gen"
import { LegacyGoogleClassroomCardEditor } from "cc-frontend/lib/google-classroom/legacy-card-editor"
import type EmberStore from "cc-frontend/services/ember-store"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import {
  isLessonModelsNeededForFirstEdit,
  type ModelsNeededForFirstEdit,
} from "cc-frontend/services/lesson-editor"
import type ModalManager from "cc-frontend/services/modal-manager"
import { find, includes } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./add-button.module.css"

interface Sig {
  Args: {
    cardStackId: string
    isFullScreen?: boolean
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
    permissions: Array<Permission>
    planbook: Planbook
    showSimpleAddCard?: boolean
    showEditTemplateButton?: boolean
    sortedCards?: Array<Card>
    dateString: string
    usedAs: CardStack["attributes"]["usedAs"]
  }
}

export default class LessonAddButtonComponent extends Component<Sig> {
  readonly styles = styles

  @service declare lessonEditor: LessonEditor
  @service("ember-store") declare store: EmberStore
  @service declare modalManager: ModalManager

  @tracked canShowAddCardMenu = false

  @action
  toggleCardMenu() {
    this.canShowAddCardMenu = !this.canShowAddCardMenu
  }

  @action
  addCardToBottom(type: LessonCard["type"], postType?: GoogleClassroomPostType) {
    let validPostTypes: Array<GoogleClassroomPostType> = [
      "ASSIGNMENT",
      "ANNOUNCEMENT",
      "MATERIAL",
      "SHORT_ANSWER_QUESTION",
      "MULTIPLE_CHOICE_QUESTION",
    ]
    // I added this check as the TS isn't doing the check through multiple template
    // boundaries and I want to make sure we don't screw it up with a typo
    if (postType && includes(validPostTypes, postType) === false)
      throw Error(`You specified a post type that can't be created; ${postType}`)
    this.canShowAddCardMenu = false
    let id = ObjectId.create()
    this.lessonEditor.addCardToBottom(
      this.args.modelsNeededForFirstEdit,
      this.args.cardStackId,
      id,
      type,
      postType
    )

    let newCard = find(this.args.sortedCards, (card) => card.id == id)

    if (!newCard) return

    if (newCard.type === "card-google-classroom") {
      this.openGoogleClassroomPostSidebar(newCard)
    } else {
      scheduleOnce("afterRender", this, () => {
        let $sectionTitle = $(`#lesson-section-${id} .lesson-section__title`)
        if ($sectionTitle) $sectionTitle.focus()
      })
    }
  }

  @action openGoogleClassroomPostSidebar(card: CardGoogleClassroom) {
    if (
      card.type === "card-google-classroom" &&
      this.args.usedAs === "lesson" &&
      includes(this.args.permissions, "can-edit-plan")
    ) {
      if (document && document.activeElement) (document.activeElement as HTMLElement).blur()
      this.store.find("planbook", this.args.planbook.id).then((planbook) => {
        this.modalManager.openModal("planner/modals/google-classroom", {
          cardEditor: new LegacyGoogleClassroomCardEditor({
            store: this.store,
            lessonEditor: this.lessonEditor,
            modelsNeededForFirstEdit: this.args.modelsNeededForFirstEdit,
            cardStackId: this.args.cardStackId,
            cardId: card.id,
            dateString: this.args.dateString,
          }),
          dateString: this.args.dateString,
          cardStackId: this.args.cardStackId,
          planbook: planbook,
        })
      })
    }
  }

  @action
  editTemplate() {
    // This button is only shown for lessons, whose models carry the rotationCalendar.
    const models = this.args.modelsNeededForFirstEdit
    if (!isLessonModelsNeededForFirstEdit(models)) return
    this.modalManager.openModal("planner/planbook/create-edit/edit-modal", {
      planbook: this.args.planbook,
      rotationCalendar: models.rotationCalendar,
      screen: "STATE_TEMPLATES_BY_COURSE",
    })
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::CardStacks::Cards::AddButton": typeof LessonAddButtonComponent
  }
}
